import axiosInstance from '@/api/axiosInstance';
import { API_PREFIX } from '@/api/endpoints/api.constants';

/* ── Types ────────────────────────────────────────────────────── */

export interface DashboardRead {
  id: number;
  name: string;
  published: boolean;
  allowguest: boolean;
  locked: boolean;
  tenant_id: number | null;
  availability: string;
  sortorder: number;
  created_at: string;
  updated_at: string;
  tenant_name: string | null;
  cohort_ids: number[];
  widget_count: number;
}

export interface DashboardCreate {
  name: string;
  published?: boolean;
  allowguest?: boolean;
  locked?: boolean;
  tenant_id?: number | null;
  availability?: string;
  sortorder?: number;
  cohort_ids?: number[];
}

export interface DashboardUpdate {
  name?: string;
  published?: boolean;
  allowguest?: boolean;
  locked?: boolean;
  tenant_id?: number | null;
  availability?: string;
  sortorder?: number;
  cohort_ids?: number[];
}

export interface DashboardLayoutRead {
  id: number;
  dashboard_id: number;
  region: string;
  layouttype: string;
  sortorder: number;
}

export interface DashboardLayoutUpdate {
  region: string;
  layouttype: string;
  sortorder?: number;
}

export interface DashboardWidgetRead {
  id: number;
  dashboard_id: number;
  widget_type: string;
  region: string;
  column_index: number;
  sortorder: number;
  config: Record<string, unknown> | null;
  visible: boolean;
}

export interface DashboardWidgetCreate {
  widget_type: string;
  region: string;
  column_index?: number;
  sortorder?: number;
  config?: Record<string, unknown>;
  visible?: boolean;
}

export interface DashboardWidgetUpdate {
  region?: string;
  column_index?: number;
  sortorder?: number;
  config?: Record<string, unknown>;
  visible?: boolean;
}

export interface DashboardLanding {
  id: number;
  name: string;
  locked: boolean;
  layouts: DashboardLayoutRead[];
  widgets: DashboardWidgetRead[];
}

/* ── API ──────────────────────────────────────────────────────── */

const BASE = '/v1/dashboards';

export const DashboardsApi = {
  // CRUD
  async list(): Promise<DashboardRead[]> {
    const r = await axiosInstance.get(`${BASE}/`);
    return r.data ?? [];
  },
  async get(id: number): Promise<DashboardRead> {
    const r = await axiosInstance.get(`${BASE}/${id}`);
    return r.data;
  },
  async create(data: DashboardCreate): Promise<DashboardRead> {
    const r = await axiosInstance.post(`${BASE}/`, data);
    return r.data;
  },
  async update(id: number, data: DashboardUpdate): Promise<DashboardRead> {
    const r = await axiosInstance.put(`${BASE}/${id}`, data);
    return r.data;
  },
  async remove(id: number): Promise<void> {
    await axiosInstance.delete(`${BASE}/${id}`);
  },
  async duplicate(id: number): Promise<DashboardRead> {
    const r = await axiosInstance.post(`${BASE}/${id}/duplicate`);
    return r.data;
  },
  async togglePublish(id: number): Promise<DashboardRead> {
    const r = await axiosInstance.put(`${BASE}/${id}/publish`);
    return r.data;
  },

  // Layout
  async getLayouts(id: number): Promise<DashboardLayoutRead[]> {
    const r = await axiosInstance.get(`${BASE}/${id}/layout`);
    return r.data ?? [];
  },
  async saveLayouts(id: number, layouts: DashboardLayoutUpdate[]): Promise<DashboardLayoutRead[]> {
    const r = await axiosInstance.put(`${BASE}/${id}/layout`, { layouts });
    return r.data ?? [];
  },

  // Widgets
  async getWidgets(id: number): Promise<DashboardWidgetRead[]> {
    const r = await axiosInstance.get(`${BASE}/${id}/widgets`);
    return r.data ?? [];
  },
  async addWidget(id: number, data: DashboardWidgetCreate): Promise<DashboardWidgetRead> {
    const r = await axiosInstance.post(`${BASE}/${id}/widgets`, data);
    return r.data;
  },
  async updateWidget(widgetId: number, data: DashboardWidgetUpdate): Promise<DashboardWidgetRead> {
    const r = await axiosInstance.put(`${API_PREFIX}/dashboard-widgets/${widgetId}`, data);
    return r.data;
  },
  async removeWidget(widgetId: number): Promise<void> {
    await axiosInstance.delete(`${API_PREFIX}/dashboard-widgets/${widgetId}`);
  },

  // Landing
  async getLanding(): Promise<DashboardLanding | null> {
    const r = await axiosInstance.get(`${API_PREFIX}/dashboard/landing`);
    return r.data ?? null;
  },
  async getLandingById(dashboardId: number): Promise<DashboardLanding | null> {
    const r = await axiosInstance.get(`${API_PREFIX}/dashboard/landing/${dashboardId}`);
    return r.data ?? null;
  },
  async getGuest(): Promise<DashboardLanding | null> {
    try {
      const r = await axiosInstance.get(`${API_PREFIX}/dashboard/guest`);
      return r.data ?? null;
    } catch {
      return null;
    }
  },
  /** Page-layout dashboards (slug='profile' powers the user profile page).
   *  Auto-creates the default layout server-side on first request. */
  async getBySlug(slug: string): Promise<DashboardLanding | null> {
    const r = await axiosInstance.get(`${API_PREFIX}/dashboard/by-slug/${slug}`);
    return r.data ?? null;
  },
};
